// src/components/pipelines/PipelineEditDrawer.js
// ============================================================================
// Edit an existing pipeline's clearance-relevant data:
//   depth_m, depth_source, status, pressure_zone
//
// WHY THIS EXISTS
// ---------------
// CreatePipelineForm captures these at registration time, but the register is
// mostly pre-existing mains that were entered before those columns existed.
// Without an edit path the only way to record a surveyed depth was to delete
// the pipeline and re-enter it — which destroys the original record.
//
// depth_m and status are read directly by the 4D subsurface clearance scan:
//   • NULL depth  → flagged at every excavation depth (safe, but noisy enough
//                   that crews stop reading the results)
//   • decommissioned / abandoned / removed → excluded from scans entirely
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
  DrawerCloseButton, DrawerFooter, Button, VStack, HStack, Box, Text,
  FormControl, FormLabel, FormHelperText, FormErrorMessage, Input, Select,
  Icon, Badge, Divider, Alert, AlertIcon, AlertTitle, AlertDescription,
  SimpleGrid, useColorModeValue, useToast, Tooltip, Tag, TagLabel,
} from '@chakra-ui/react';
import {
  MdRouter, MdWarning, MdCheckCircle, MdVerifiedUser, MdErrorOutline,
} from 'react-icons/md';
import { FaArrowDown, FaShieldAlt, FaClipboardCheck } from 'react-icons/fa';

import * as PipelineService from '../../services/pipelineService';
import { PIPELINE_STATUSES, DEPTH_SOURCES, validatePipelineDepth } from '../../services/pipelineService';

const EMPTY_FORM = { depth_m: '', depth_source: '', status: 'active', pressure_zone: '', material: '', area_code: '' };

function PipelineEditDrawer({ isOpen, onClose, pipeline, onSaved }) {
  // All useColorModeValue calls hoisted to the top level.
  const headerBorder = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.800');
  const panelBg = useColorModeValue('gray.50', 'gray.900');
  const helperColor = useColorModeValue('gray.600', 'gray.400');
  const okColor = useColorModeValue('green.600', 'green.300');
  const badColor = useColorModeValue('red.600', 'red.300');

  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [depthError, setDepthError] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (!isOpen || !pipeline) return;
    setForm({
      depth_m: pipeline.depth_m ?? '',
      depth_source: pipeline.depth_source ?? '',
      status: pipeline.status ?? 'active',
      pressure_zone: pipeline.pressure_zone ?? '',
      material: pipeline.material ?? '',
      area_code: pipeline.area_code ?? '',
    });
    setDepthError('');
  }, [isOpen, pipeline]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'depth_m') setDepthError('');
  }, []);

  const depthFeedback = useMemo(() => validatePipelineDepth(form.depth_m), [form.depth_m]);

  const selectedStatus = useMemo(
    () => PIPELINE_STATUSES.find(s => s.value === form.status),
    [form.status]
  );

  // Live preview of what this edit does to clearance visibility, so the
  // consequence is visible before the save rather than discovered later.
  const clearancePreview = useMemo(() => {
    if (!pipeline) return null;
    const sridOk = pipeline.geometry_srid_ok !== false;
    const statusScanned = selectedStatus ? selectedStatus.scanned : true;
    const hasDepth = form.depth_m !== '' && form.depth_m !== null;

    if (!sridOk) {
      return {
        tone: 'error',
        title: 'Geometry unusable by the clearance scan',
        detail: 'This pipeline\'s geometry is not SRID 4326, so the scan raises an error against it and returns INDETERMINATE. Editing depth will not fix that — run the SRID repair migration.',
      };
    }
    if (!statusScanned) {
      return {
        tone: 'warning',
        title: `Status "${selectedStatus.label}" removes this main from clearance scans`,
        detail: 'The record is retained, but excavation checks will no longer flag it. Only use this for mains that are genuinely no longer in the ground or no longer a strike hazard.',
      };
    }
    if (!hasDepth) {
      return {
        tone: 'info',
        title: 'No cover depth recorded',
        detail: 'The scan treats unknown depth as in-scope, so this main is flagged on every excavation regardless of dig depth. Safe, but it buries the real conflicts in noise.',
      };
    }
    return {
      tone: 'success',
      title: 'Visible to clearance scans with a recorded depth',
      detail: `Flagged only when an excavation reaches ${form.depth_m} m (plus the vertical safety buffer).`,
    };
  }, [pipeline, selectedStatus, form.depth_m]);

  const handleSave = useCallback(async () => {
    if (!pipeline) return;

    const depth = validatePipelineDepth(form.depth_m);
    if (depth.errors.length > 0) {
      setDepthError(depth.errors.join(' '));
      return;
    }

    setIsSaving(true);
    try {
      // Partial update — only the clearance fields. Geometry and name are not
      // touched, so an edit here cannot accidentally wipe the line.
      const updated = await PipelineService.updatePipeline(
        pipeline.id,
        {
          depth_m: form.depth_m === '' ? null : Number(form.depth_m),
          depth_source: form.depth_source || null,
          status: form.status,
          pressure_zone: form.pressure_zone || null,
          material: form.material || null,
          area_code: form.area_code || null,
        },
        { merge: false }   // the backend does a true partial update
      );

      const warnings = updated?.warnings || [];
      toast({
        title: 'Pipeline Updated',
        description: warnings.length > 0 ? warnings.join(' ') : `Clearance data saved for ${pipeline.name}.`,
        status: warnings.length > 0 ? 'warning' : 'success',
        duration: warnings.length > 0 ? 8000 : 3000,
        isClosable: true,
      });

      if (onSaved) onSaved(updated);
      onClose();
    } catch (err) {
      const detail = err.details?.length ? err.details.join(' ') : err.message;
      toast({ title: 'Update Failed', description: detail, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  }, [pipeline, form, onSaved, onClose, toast]);

  if (!pipeline) return null;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={headerBorder}>
          <HStack spacing={3}>
            <Box p={2} bg="purple.500" borderRadius="lg"><Icon as={MdRouter} color="white" boxSize={5} /></Box>
            <VStack align="start" spacing={0}>
              <Text fontWeight="800" fontSize="md">{pipeline.name || `Pipeline ${pipeline.id}`}</Text>
              <HStack spacing={2} fontSize="xs" color={helperColor}>
                <Text>ID {pipeline.id}</Text>
                {pipeline.diameter_inches && <><Text>•</Text><Text>{pipeline.diameter_inches}"</Text></>}
                {pipeline.material && <><Text>•</Text><Text>{pipeline.material}</Text></>}
              </HStack>
            </VStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody py={5}>
          <VStack spacing={5} align="stretch">

            {/* Clearance consequence preview */}
            {clearancePreview && (
              <Alert
                status={clearancePreview.tone}
                borderRadius="lg"
                flexDirection="column"
                alignItems="flex-start"
                fontSize="sm"
              >
                <HStack>
                  <AlertIcon as={clearancePreview.tone === 'success' ? MdVerifiedUser : clearancePreview.tone === 'error' ? MdErrorOutline : MdWarning} />
                  <AlertTitle fontSize="sm">{clearancePreview.title}</AlertTitle>
                </HStack>
                <AlertDescription fontSize="xs" mt={1} pl={7}>{clearancePreview.detail}</AlertDescription>
              </Alert>
            )}

            <Box>
              <HStack mb={3} spacing={2}>
                <Icon as={FaShieldAlt} color="orange.400" />
                <Text fontSize="sm" fontWeight="800">Excavation Clearance Data</Text>
                <Badge colorScheme="orange" fontSize="9px">SAFETY</Badge>
              </HStack>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isInvalid={!!depthError}>
                  <FormLabel fontSize="sm" fontWeight="700">Cover Depth (m)</FormLabel>
                  <Input
                    type="number" step="0.05" min="0" max="30"
                    name="depth_m" placeholder="e.g., 1.20"
                    value={form.depth_m} onChange={handleChange}
                    bg={inputBg} borderRadius="lg"
                  />
                  {depthError ? (
                    <FormErrorMessage fontSize="xs">{depthError}</FormErrorMessage>
                  ) : depthFeedback.warnings.length > 0 ? (
                    <FormHelperText fontSize="xs" color="orange.400">{depthFeedback.warnings[0]}</FormHelperText>
                  ) : (
                    <FormHelperText fontSize="xs" color={helperColor}>Surface to crown, in metres.</FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Depth Source</FormLabel>
                  <Select name="depth_source" value={form.depth_source} onChange={handleChange} bg={inputBg} borderRadius="lg">
                    <option value="">Not recorded</option>
                    {DEPTH_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  <FormHelperText fontSize="xs" color={helperColor}>A locate and an as-built are different confidence.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Status</FormLabel>
                  <Select name="status" value={form.status} onChange={handleChange} bg={inputBg} borderRadius="lg">
                    {PIPELINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  {selectedStatus && !selectedStatus.scanned && (
                    <FormHelperText fontSize="xs" color={badColor} fontWeight="600">
                      Excluded from clearance scans.
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Pressure Zone</FormLabel>
                  <Input name="pressure_zone" placeholder="e.g., ZONE-2" value={form.pressure_zone} onChange={handleChange} bg={inputBg} borderRadius="lg" />
                  <FormHelperText fontSize="xs" color={helperColor}>Used by the cascade simulation.</FormHelperText>
                </FormControl>
              </SimpleGrid>
            </Box>

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="800" mb={3}>Asset Details</Text>
              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Material</FormLabel>
                  <Input name="material" placeholder="e.g., Ductile Iron" value={form.material} onChange={handleChange} bg={inputBg} borderRadius="lg" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Area Zone</FormLabel>
                  <Input name="area_code" placeholder="e.g., SECTOR-7" value={form.area_code} onChange={handleChange} bg={inputBg} borderRadius="lg" />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Read-only geometry facts. Geometry is edited on the map, not here —
                a text field is the wrong instrument for a line on the ground. */}
            <Box p={3} bg={panelBg} borderRadius="lg">
              <Text fontSize="xs" fontWeight="700" mb={2}>Geometry</Text>
              <HStack spacing={4} fontSize="xs" flexWrap="wrap">
                <HStack spacing={1}>
                  <Icon as={pipeline.geometry_srid_ok === false ? MdWarning : MdCheckCircle}
                        color={pipeline.geometry_srid_ok === false ? badColor : okColor} />
                  <Text>SRID {pipeline.geometry_srid ?? '—'}</Text>
                </HStack>
                <Text color={helperColor}>
                  {Array.isArray(pipeline.path_coordinates)
                    ? `${pipeline.path_coordinates.length} vertices`
                    : 'No path data'}
                </Text>
                {pipeline.install_date && (
                  <Text color={helperColor}>Installed {String(pipeline.install_date).slice(0, 10)}</Text>
                )}
              </HStack>
              <Text fontSize="10px" color={helperColor} mt={2}>
                Redraw the route on the map to change geometry.
              </Text>
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={headerBorder} gap={3}>
          <Button variant="ghost" onClick={onClose} borderRadius="lg">Cancel</Button>
          <Button colorScheme="purple" onClick={handleSave} isLoading={isSaving} borderRadius="lg" px={6}>
            Save Changes
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

PipelineEditDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pipeline: PropTypes.object,
  onSaved: PropTypes.func,
};

export default PipelineEditDrawer;